import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './ThemeProvider';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login.jsx';
import Register from './components/Register.jsx';
import PredictionPage from './components/PredictionPage.jsx';
import SettingsPage from './components/SettingsPage.jsx';
import PersonasPage from './components/PersonasPage.jsx';
import AddEventPage from './components/AddEventPage.jsx';
import DisclaimerPage from './components/DisclaimerPage.jsx';
import Navigation from './components/Navigation.jsx';
import UpgradeModal from './components/UpgradeModal.jsx';
import './App.css';
import { applyTheme, getTheme } from './themes';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {

    const theme = process.env.REACT_APP_THEME || 'purple';

    console.log('Applying theme:', theme);
    applyTheme(getTheme(theme));

    window.currentTheme = theme;

    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const handleUpgrade = (tier) => {
    // Handle Stripe checkout
    fetch(`${API_URL}/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ tier })
    })
    .then(res => res.json())
    .then(data => {
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="app">
        {user && <Navigation user={user} logout={logout} />}
        
        <Routes>
          <Route 
            path="/login" 
            element={!user ? <Login onLogin={login} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/register" 
            element={!user ? <Register onRegister={login} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/" 
            element={user ? (
              <PredictionPage 
                user={user} 
                token={token}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/settings" 
            element={user ? (
              <SettingsPage 
                user={user} 
                token={token}
                onUpdate={fetchUserProfile}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/personas" 
            element={user ? (
              <PersonasPage 
                user={user} 
                token={token}
                onUpdate={fetchUserProfile}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />} 
          />
          <Route
            path="/add-event"
            element={user ? (
              <AddEventPage
                user={user}
                token={token}
                onUpdate={fetchUserProfile}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />}
          />
          <Route
            path="/disclaimer"
            element={<DisclaimerPage />}
          />
        </Routes>

        {showUpgrade && (
          <UpgradeModal
            currentTier={user?.tier}
            onClose={() => setShowUpgrade(false)}
            onUpgrade={handleUpgrade}
          />
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;